import { Controller } from "@hotwired/stimulus"
import mapboxManager from "../../lib/mapbox_manager"

// Generic Mapbox map shell. Creates a map, optionally adds one marker, and emits
// map:ready so page-specific controllers can attach overlays (radius circles, job pins, etc.).
//
// Future multi-marker pages (e.g. jobs index) can use the same map:ready event with a
// dedicated controller; center/zoom for many jobs stay in that controller for now.
export default class extends Controller {
  static values = {
    mapId: String,
    lat: Number,
    lng: Number,
    accessToken: String,
    zoom: { type: Number, default: 10 },
    showMarker: { type: Boolean, default: true },
    markerTitle: { type: String, default: "" }
  }

  connect() {
    this.abortController = new AbortController()
    this.initialize()
  }

  disconnect() {
    this.abortController.abort()
    this.dispatch("disconnect", { detail: this.readyDetail })
    this.teardown()
  }

  async initialize() {
    const { signal } = this.abortController

    try {
      if (signal.aborted || !this.hasCoordinates) return

      const map = await this.createMap()

      if (signal.aborted) {
        this.teardown()
        return
      }

      if (this.showMarkerValue) {
        this.addMarker(map)
      }

      this.readyDetail = {
        map,
        lat: this.latValue,
        lng: this.lngValue,
        mapId: this.resolvedMapId,
        element: this.element
      }

      this.dispatch("ready", { detail: this.readyDetail })
    } catch (error) {
      if (!signal.aborted) {
        console.error("Failed to initialize map:", error)
        this.element.innerHTML =
          '<div class="flex items-center justify-center h-full text-gray-500">Failed to load map</div>'
      }
    }
  }

  get hasCoordinates() {
    return Number.isFinite(this.latValue) && Number.isFinite(this.lngValue)
  }

  get resolvedMapId() {
    return this.mapIdValue || this.element.id || "map"
  }

  teardown() {
    mapboxManager.destroyMap(this.resolvedMapId)
    this.readyDetail = null
  }

  async createMap() {
    await mapboxManager.loadAPI(this.accessTokenValue)

    const map = mapboxManager.createMapWithCache(
      this.element,
      {
        style: "mapbox://styles/mapbox/streets-v12",
        center: [this.lngValue, this.latValue],
        zoom: this.zoomValue
      },
      this.resolvedMapId
    )

    if (window.mapboxgl?.NavigationControl) {
      map.addControl(new window.mapboxgl.NavigationControl(), "top-right")
    }

    await new Promise((resolve) => {
      if (map.loaded()) resolve()
      else map.once("load", resolve)
    })

    return map
  }

  addMarker(map) {
    const el = document.createElement("div")
    el.className = "mapbox-marker"
    el.style.width = "32px"
    el.style.height = "32px"
    el.style.cursor = "pointer"

    const img = document.createElement("img")
    img.src = "/map-pin.png"
    img.style.width = "100%"
    img.style.height = "100%"
    img.style.display = "block"
    img.alt = this.markerTitleValue || "Location"
    el.appendChild(img)

    const marker = mapboxManager.createMarker({ element: el, anchor: "bottom" })
    marker.setLngLat([this.lngValue, this.latValue])
    marker.addTo(map)
  }
}
